import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import { ClientAccountSummary, ClientAccountPaymentDTO, ClientAccountPaymentRequest } from '../types/clientAccount';

const baseUrl = '/v1/clientes-conta';

function toSummary(obj: any, clienteId: string): ClientAccountSummary {
  const saldo = Number(obj?.saldoDevedor ?? obj?.saldo ?? obj?.totalEmAberto ?? 0);
  return {
    clienteId,
    nomeCliente: obj?.nomeCliente ?? obj?.clienteNome ?? obj?.nome,
    cpf: obj?.cpf ?? obj?.documento,
    saldoDevedor: isNaN(saldo) ? 0 : +saldo,
    atualizadoEm: obj?.atualizadoEm ?? obj?.updatedAt,
  };
}

function toPayment(obj: any, clienteId: string): ClientAccountPaymentDTO {
  return {
    id: String(obj?.id ?? obj?.uuid ?? obj?.codigo ?? Math.random().toString(36).slice(2)),
    clienteId: String(obj?.clienteId ?? clienteId),
    valor: Number(obj?.valor ?? 0),
    tipoPagamentoId: String(obj?.tipoPagamentoId ?? obj?.tipo_pagamento_id ?? ''),
    tipoPagamentoNome: obj?.tipoPagamentoNome ?? obj?.tipo_pagamento_nome ?? obj?.forma ?? obj?.tipo,
    numeroDocumento: obj?.numeroDocumento ?? obj?.documento,
    observacoes: obj?.observacoes ?? obj?.observacao ?? obj?.obs,
    descricao: obj?.descricao ?? undefined,
    dataPagamento: obj?.dataPagamento ?? obj?.createdAt ?? obj?.data,
    usuarioId: obj?.usuarioId ?? obj?.userId,
    usuarioNome: obj?.usuarioNome ?? obj?.userName ?? undefined,
    status: obj?.status,
    origem: obj?.origem,
    vendaId: obj?.vendaId !== undefined ? String(obj?.vendaId) : undefined,
    vendaNumero: obj?.vendaNumero ?? undefined,
    compraId: obj?.compraId !== undefined ? String(obj?.compraId) : undefined,
    compraNumero: obj?.compraNumero ?? undefined,
    contaClienteId: obj?.contaClienteId !== undefined ? String(obj?.contaClienteId) : undefined,
    tenantId: obj?.tenantId ?? undefined,
    dataCriacao: obj?.dataCriacao ?? obj?.createdAt ?? undefined,
    dataAtualizacao: obj?.dataAtualizacao ?? obj?.updatedAt ?? undefined,
  };
}

export const clientAccountService = {
  async listAccounts(params?: { cpf?: string; cnpj?: string; nome?: string; page?: number; size?: number; sort?: string | string[] }): Promise<ApiResponse<PageResponse<ClientAccountSummary>>> {
    const sp = new URLSearchParams();
    if (params?.cpf) sp.append('cpf', params.cpf);
    if (params?.cnpj) sp.append('cnpj', params.cnpj);
    if (params?.nome) sp.append('nome', params.nome);
    const sortArr = Array.isArray(params?.sort) ? params!.sort! : (params?.sort ? [params.sort] : []);
    const pageable = {
      page: Number(params?.page ?? 0),
      size: Number(params?.size ?? 10),
      sort: sortArr,
    } as any;
    sp.append('pageable', JSON.stringify(pageable));
    const url = `/v1/clientes-conta?${sp.toString()}`;
    const res = await api.get<ApiResponse<any>>(url);
    const payload = res.data;
    const rawPage = payload?.data ?? payload;
    const contentRaw = Array.isArray(rawPage?.content) ? rawPage.content : (Array.isArray(rawPage) ? rawPage : []);
    const mapped = contentRaw.map((x: any) => {
      const cliente = x?.cliente ?? {};
      const nome = cliente?.nome ?? x?.clienteNome ?? x?.nomeCliente ?? x?.nome;
      const doc = cliente?.cpf ?? cliente?.cnpj ?? x?.cpf ?? x?.documento;
      const saldo = Number(x?.saldoDevedor ?? x?.saldo ?? 0);
      return {
        clienteId: String(x?.clienteId ?? cliente?.id ?? ''),
        nomeCliente: nome,
        cpf: doc,
        saldoDevedor: isNaN(saldo) ? 0 : +saldo,
        atualizadoEm: x?.ultimaAtualizacao ?? x?.atualizadoEm ?? x?.updatedAt,
      } as ClientAccountSummary;
    });
    const page: PageResponse<ClientAccountSummary> = {
      content: mapped,
      totalElements: Number(rawPage?.totalElements ?? rawPage?.numberOfElements ?? mapped.length),
      totalPages: Number(rawPage?.totalPages ?? 1),
      size: Number(rawPage?.size ?? pageable.size),
      number: Number(rawPage?.number ?? pageable.page),
      first: Boolean(rawPage?.first ?? (Number(rawPage?.number ?? pageable.page) === 0)),
      last: Boolean(rawPage?.last ?? (Number(rawPage?.number ?? pageable.page) + 1 >= Number(rawPage?.totalPages ?? 1))),
    };
    return { ...payload, data: page } as any;
  },
  async getAccount(clienteId: string, params?: { nomeCliente?: string; cpf?: string }): Promise<ApiResponse<ClientAccountSummary>> {
    const url = `${baseUrl}/${clienteId}`;
    const res = await api.get<ApiResponse<any>>(url);
    const payload = res.data;
    const summary = toSummary(payload?.data ?? payload, clienteId);
    return { ...payload, data: summary } as any;
  },

  async listPayments(clienteId: string, params?: { page?: number; size?: number; sort?: string }): Promise<ApiResponse<PageResponse<ClientAccountPaymentDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.append('page', String(params.page));
    if (params?.size !== undefined) searchParams.append('size', String(params.size));
    if (params?.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString();
    const url = qs ? `${baseUrl}/${clienteId}/pagamentos?${qs}` : `${baseUrl}/${clienteId}/pagamentos`;
    const res = await api.get<ApiResponse<any>>(url);
    const payload = res.data;
    const raw = payload?.data ?? payload;
    const content: any[] = Array.isArray(raw?.content) ? raw.content : Array.isArray(raw) ? raw : [];
    const mapped = content.map((x: any) => toPayment(x, clienteId));
    const page: PageResponse<ClientAccountPaymentDTO> = {
      content: mapped,
      totalElements: Number(raw?.totalElements ?? raw?.numberOfElements ?? mapped.length),
      totalPages: Number(raw?.totalPages ?? 1),
      size: Number(raw?.size ?? (params?.size ?? mapped.length)),
      number: Number(raw?.number ?? (params?.page ?? 0)),
      first: Boolean(raw?.first ?? (Number(raw?.number ?? 0) === 0)),
      last: Boolean(raw?.last ?? (Number(raw?.number ?? 0) + 1 >= Number(raw?.totalPages ?? 1))),
    };
    return { ...payload, data: page } as any;
  },

  async registerPayment(clienteId: string, data: ClientAccountPaymentRequest): Promise<ApiResponse<ClientAccountPaymentDTO>> {
    const res = await api.post<ApiResponse<any>>(`${baseUrl}/${clienteId}/pagamentos`, data);
    const payload = res.data;
    const mapped = toPayment(payload?.data ?? payload, clienteId);
    return { ...payload, data: mapped } as any;
  },
};

export default clientAccountService;